import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Mail, Phone, Calendar, ShoppingCart, Clock, CheckCircle2, Trash2, Upload } from "lucide-react";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total_leads: 0, unique_emails: 0, converted: 0, pending: 0,
    conversion_pct: 0, deleted_anonymized: 0, duplicates: 0,
  });
  const [sources, setSources] = useState({ checkout_form: 0, csv_import: 0, manual: 0, other: 0, total: 0 });
  const [source, setSource] = useState("checkout_form"); // checkout_form | csv_import | manual | any
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | purchased | pending
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (filter !== "all") params.set("status", filter);
    if (includeDeleted) params.set("include_deleted", "true");
    params.set("source", source);
    const statsParams = new URLSearchParams();
    if (includeDeleted) statsParams.set("include_deleted", "true");
    statsParams.set("source", source);
    const [{ data: l }, { data: s }, { data: src }] = await Promise.all([
      api.get(`/admin/leads?${params.toString()}`),
      api.get(`/admin/leads/stats/summary?${statsParams.toString()}`),
      api.get(`/admin/leads/sources`),
    ]);
    setLeads(l);
    setStats(s);
    setSources(src);
  };

  useEffect(() => { load(); }, [filter, includeDeleted, source]);

  const openDetail = async (lead) => {
    const { data } = await api.get(`/admin/leads/${lead.id}`);
    setSelected(data);
  };

  const deleteLead = async (e, id) => {
    e.stopPropagation();
    if (!confirm("حذف هذا العميل نهائيًا؟")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      await load();
    } catch (err) { alert("فشل الحذف"); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`تم الاستيراد: ${data.inserted} جديد، ${data.skipped_duplicates} مكرر، ${data.skipped_invalid} غير صالح`);
      await load();
    } catch (err) { alert("فشل الاستيراد: " + (err?.response?.data?.detail || "خطأ")); }
    e.target.value = "";
  };

  const dateShort = (iso) => iso ? new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }) : "—";
  const statusChip = (s) => {
    if (s === "purchased") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">مُشترى</span>;
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">لم يشترِ بعد</span>;
  };
  const emailStatusChip = (s) => {
    if (!s) return null;
    const map = {
      sent: "bg-blue-100 text-blue-800", mock: "bg-gray-100 text-gray-700",
      delivered: "bg-green-100 text-green-800", opened: "bg-emerald-100 text-emerald-900",
      bounced: "bg-red-100 text-red-700", failed: "bg-red-100 text-red-700",
      complained: "bg-orange-100 text-orange-800",
      pending: "bg-yellow-100 text-yellow-800", skipped_purchased: "bg-blue-100 text-blue-700",
      cancelled: "bg-gray-100 text-gray-600",
    };
    const labels = { sent: "مُرسل", delivered: "وصل", opened: "قُرئ", bounced: "ارتد", failed: "فشل",
      complained: "شكوى", pending: "قيد الإرسال", skipped_purchased: "أُلغي بعد الشراء", mock: "محاكاة", cancelled: "ملغى" };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full ${map[s] || "bg-foreground/10"}`}>{labels[s] || s}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">العملاء المحتملون (Leads)</h1>
          <p className="text-foreground/60 mt-2 text-sm">
            {source === "checkout_form" && "عملاء ملأوا نموذج الخطوة الأولى — سواء أكملوا الدفع أم لا."}
            {source === "csv_import" && "عملاء استوردتَهم من ملف CSV أو Excel (جمهور تسويقي)."}
            {source === "manual" && "عملاء أضفتَهم يدويًا."}
            {source === "any" && "كل العملاء المحتملين من جميع المصادر."}
          </p>
        </div>
        <div className="flex gap-2">
          <label className="inline-flex">
            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={importFile} data-testid="import-file" />
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer hover:bg-foreground/5 text-sm">
              <Upload className="h-4 w-4" /> استيراد CSV/Excel
            </span>
          </label>
        </div>
      </div>

      {/* Source tabs — separates form leads from imported audience so numbers stay honest */}
      <div className="flex flex-wrap gap-2 border-b border-foreground/10 pb-3">
        {[
          { k: "checkout_form", l: "من نموذج الشراء", hint: "عملاء ملأوا الخطوة الأولى فعليًا", count: sources.checkout_form },
          { k: "csv_import", l: "مستوردون (CSV/Excel)", hint: "أضفتهم من ملف خارجي", count: sources.csv_import },
          { k: "manual", l: "يدوي", hint: "أضفتهم يدويًا", count: sources.manual, hideIfZero: true },
          { k: "any", l: "الكل مجتمعًا", hint: "كل المصادر معًا", count: sources.total },
        ].filter((t) => !t.hideIfZero || t.count > 0).map((t) => (
          <button
            key={t.k}
            onClick={() => setSource(t.k)}
            className={`px-3 py-1.5 rounded-md text-sm border transition ${
              source === t.k
                ? "bg-black text-white border-foreground"
                : "bg-white text-foreground/70 border-foreground/10 hover:bg-foreground/5"
            }`}
            title={t.hint}
            data-testid={`source-tab-${t.k}`}
          >
            {t.l} <span className="opacity-60 text-xs">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Stats — deduped by email so the numbers match what you see below */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBox
          label="عدد العملاء (فريد)"
          value={stats.unique_emails}
          hint={stats.duplicates > 0 ? `+${stats.duplicates} تسجيلات مكررة` : `إجمالي التسجيلات: ${stats.total_leads}`}
          icon={Mail}
        />
        <StatBox
          label="أكملوا الشراء"
          value={stats.converted}
          hint="عملاء دفعوا فعليًا (بريد فريد)"
          value_test="stat-converted"
          icon={CheckCircle2}
          color="text-green-700"
        />
        <StatBox
          label="لم يشتروا بعد"
          value={stats.pending}
          hint="عملاء لم يُكملوا الدفع (بريد فريد)"
          icon={Clock}
          color="text-yellow-700"
        />
        <StatBox
          label="نسبة التحويل"
          value={`${stats.conversion_pct}%`}
          hint={`${stats.converted} من ${stats.unique_emails}`}
          icon={ShoppingCart}
          color="text-blue-700"
        />
      </div>

      {/* Deleted / duplicates transparency row */}
      {(stats.deleted_anonymized > 0 || stats.duplicates > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground/60">
          {stats.duplicates > 0 && (
            <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900">
              ⚠️ {stats.duplicates} تسجيلات مكررة (نفس البريد سُجّل أكثر من مرة) — الأرقام أعلاه تعتمد البريد الفريد.
            </span>
          )}
          {stats.deleted_anonymized > 0 && (
            <label className="inline-flex items-center gap-2 px-2 py-1 rounded bg-slate-50 border border-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                data-testid="toggle-include-deleted-leads"
              />
              <span>عرض العملاء المحذوفين ({stats.deleted_anonymized})</span>
            </label>
          )}
        </div>
      )}

      {/* Search + filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            className="pe-10" placeholder="ابحث بالاسم أو البريد أو الجوال..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            data-testid="leads-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { k: "all", l: "الكل" },
            { k: "purchased", l: "مُشتروا" },
            { k: "pending", l: "لم يشترِ" },
          ].map((f) => (
            <Button key={f.k} size="sm" variant={filter === f.k ? "default" : "outline"} onClick={() => setFilter(f.k)}>
              {f.l}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={load}>بحث</Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {leads.length === 0 && <Card className="p-8 text-center text-foreground/50">لا توجد نتائج.</Card>}
        {leads.map((l) => (
          <Card key={l.id} className="p-4 flex flex-wrap items-center gap-3 cursor-pointer hover:bg-black/[0.02]" onClick={() => openDetail(l)} data-testid={`lead-${l.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <div className="font-display font-semibold">{l.full_name}</div>
                {statusChip(l.purchase_status)}
                {emailStatusChip(l.last_email_status)}
              </div>
              <div className="text-xs text-foreground/60 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {l.email}</span>
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.whatsapp}</span>
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {dateShort(l.created_at)}</span>
                {l.emails_sent_count > 0 && <span>📧 {l.emails_sent_count} رسالة أُرسلت</span>}
                {l.scheduled_pending_count > 0 && <span>⏳ {l.scheduled_pending_count} مجدولة</span>}
              </div>
              {l.purchase_status === "purchased" && (
                <div className="text-xs text-green-700 mt-1">
                  اشترى {l.product_name} بمبلغ {l.purchase_amount} {l.purchase_currency}
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={(e) => deleteLead(e, l.id)} data-testid={`del-lead-${l.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
          </Card>
        ))}
      </div>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          {selected && <>
            <DialogHeader><DialogTitle>{selected.lead.full_name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Card className="p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-foreground/60">البريد</span><span>{selected.lead.email}</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">الجوال</span><span dir="ltr">{selected.lead.whatsapp}</span></div>
                <div className="flex justify-between"><span className="text-foreground/60">التاريخ</span><span>{dateShort(selected.lead.created_at)}</span></div>
                <div className="flex justify-between items-center"><span className="text-foreground/60">الحالة</span>
                  {selected.purchase ? statusChip("purchased") : statusChip("pending")}
                </div>
              </Card>
              {selected.purchase && (
                <Card className="p-4 bg-green-50 border-green-200 space-y-1 text-sm">
                  <div className="font-semibold text-green-900">اكتمل الشراء</div>
                  <div>المنتج: {selected.purchase.product_name}</div>
                  <div>المبلغ: {selected.purchase.amount} {selected.purchase.currency}</div>
                  <div>تاريخ الشراء: {dateShort(selected.purchase.created_at)}</div>
                </Card>
              )}
              <div>
                <h3 className="font-display font-semibold mb-2">الرسائل المُرسلة ({selected.email_logs.length})</h3>
                <div className="space-y-1">
                  {selected.email_logs.length === 0 && <p className="text-xs text-foreground/50">لا توجد.</p>}
                  {selected.email_logs.map((log, i) => (
                    <div key={i} className="text-xs border rounded-md p-2 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{log.subject}</div>
                        <div className="text-[10px] text-foreground/50">{log.template_key} • {dateShort(log.sent_at)}</div>
                      </div>
                      {emailStatusChip(log.last_event ? log.last_event.split(".")[1] : log.status)}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-display font-semibold mb-2">رسائل مجدولة ({selected.scheduled_emails.length})</h3>
                <div className="space-y-1">
                  {selected.scheduled_emails.length === 0 && <p className="text-xs text-foreground/50">لا توجد.</p>}
                  {selected.scheduled_emails.map((s, i) => (
                    <div key={i} className="text-xs border rounded-md p-2 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div>{s.template_key}</div>
                        <div className="text-[10px] text-foreground/50">مجدول: {dateShort(s.scheduled_at)}</div>
                      </div>
                      {emailStatusChip(s.status)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color = "text-foreground", hint }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-foreground/60">{label}</div>
          <div className={`text-2xl font-display font-semibold mt-1 ${color}`}>{value}</div>
          {hint && <div className="text-[10px] text-foreground/50 mt-1">{hint}</div>}
        </div>
        <div className="h-9 w-9 rounded-lg bg-foreground/5 grid place-items-center"><Icon className={`h-4 w-4 ${color}`} /></div>
      </div>
    </Card>
  );
}
